import React, { useState, useEffect } from 'react';
import { api } from '../context/AuthContext';
import {
  Title, Card, Table, Button, Group, Text, Badge, Modal,
  TextInput, NumberInput, Select, Stack, Loader, Center,
  ActionIcon, Tooltip, Checkbox, Grid, Paper
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconPencil, IconTrash, IconUsers } from '@tabler/icons-react';

const AREE = [
  { value: 'usave_fco', label: 'U-Save FCO' },
  { value: 'usave_cia', label: 'U-Save CIA' },
  { value: 'commercio_veicoli', label: 'Commercio Veicoli' },
  { value: 'consulenza_nlt', label: 'Consulenza NLT' },
  { value: 'generale', label: 'Generale' },
];

const areaLabel = (val) => AREE.find(a => a.value === val)?.label || val;

function Dipendenti() {
  const [dipendenti, setDipendenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nome: '', cognome: '', attivo: true, note: '', allocazioni: [] });
  const isMobile = useMediaQuery('(max-width: 768px)');

  useEffect(() => { loadDipendenti(); }, []);

  const loadDipendenti = async () => {
    try {
      const res = await api.get('/dipendenti');
      setDipendenti(res.data);
    } catch { notifications.show({ title: 'Errore', message: 'Impossibile caricare i dipendenti', color: 'red' }); }
    finally { setLoading(false); }
  };

  const resetForm = () => {
    setEditing(null);
    setForm({ nome: '', cognome: '', attivo: true, note: '', allocazioni: [] });
  };

  const handleEdit = (dip) => {
    setEditing(dip);
    setForm({
      nome: dip.nome || '', cognome: dip.cognome || '', attivo: dip.attivo,
      note: dip.note || '',
      allocazioni: dip.allocazioni.map(a => ({ area: a.area, percentuale: a.percentuale }))
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const totPerc = form.allocazioni.reduce((s, a) => s + (a.percentuale || 0), 0);
    if (Math.abs(totPerc - 100) > 0.01 && form.allocazioni.length > 0) {
      notifications.show({ title: 'Attenzione', message: `Le allocazioni sommano ${totPerc}% (deve essere 100%)`, color: 'yellow' });
      return;
    }
    try {
      if (editing) {
        await api.put(`/dipendenti/${editing.id}`, form);
        notifications.show({ title: 'Successo', message: 'Risorsa aggiornata', color: 'green' });
      } else {
        await api.post('/dipendenti', form);
        notifications.show({ title: 'Successo', message: 'Risorsa creata', color: 'green' });
      }
      setShowModal(false);
      resetForm();
      loadDipendenti();
    } catch { notifications.show({ title: 'Errore', message: 'Errore nel salvataggio', color: 'red' }); }
  };

  const handleDelete = async (dip) => {
    if (window.confirm(`Eliminare ${dip.nome} ${dip.cognome}?`)) {
      try {
        await api.delete(`/dipendenti/${dip.id}`);
        notifications.show({ title: 'Successo', message: 'Risorsa eliminata', color: 'green' });
        loadDipendenti();
      } catch { notifications.show({ title: 'Errore', message: 'Errore eliminazione', color: 'red' }); }
    }
  };

  const addAllocazione = () => {
    setForm({ ...form, allocazioni: [...form.allocazioni, { area: 'usave_fco', percentuale: 0 }] });
  };

  const removeAllocazione = (idx) => {
    setForm({ ...form, allocazioni: form.allocazioni.filter((_, i) => i !== idx) });
  };

  const updateAllocazione = (idx, field, value) => {
    const allocs = [...form.allocazioni];
    allocs[idx] = { ...allocs[idx], [field]: value };
    setForm({ ...form, allocazioni: allocs });
  };

  if (loading) return <Center h={400}><Loader size="lg" color="teal" /></Center>;

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Group>
          <IconUsers size={28} color="rgb(32, 83, 87)" />
          <Title order={2}>Risorse Umane</Title>
        </Group>
        <Button leftSection={<IconPlus size={18} />} onClick={() => { resetForm(); setShowModal(true); }}>
          Nuova Risorsa
        </Button>
      </Group>

      <Card shadow="sm" padding={isMobile ? 'sm' : 'lg'} radius="md" withBorder>
        <Table striped highlightOnHover style={isMobile ? { fontSize: '0.8rem' } : undefined}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Nome</Table.Th>
              {!isMobile && <Table.Th>Stato</Table.Th>}
              {!isMobile && <Table.Th>Allocazioni</Table.Th>}
              <Table.Th>Azioni</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {dipendenti.map(d => (
              <Table.Tr key={d.id} onClick={() => isMobile && handleEdit(d)} style={isMobile ? { cursor: 'pointer' } : undefined}>
                <Table.Td>
                  <Text size={isMobile ? 'xs' : 'sm'} fw={600}>{d.nome} {d.cognome}</Text>
                  {isMobile && (
                    <Badge color={d.attivo ? 'green' : 'gray'} variant="subtle" size="xs" mt={2}>
                      {d.attivo ? 'Attivo' : 'Inattivo'}
                    </Badge>
                  )}
                </Table.Td>
                {!isMobile && (
                  <Table.Td>
                    <Badge color={d.attivo ? 'green' : 'gray'} variant="subtle">
                      {d.attivo ? 'Attivo' : 'Inattivo'}
                    </Badge>
                  </Table.Td>
                )}
                {!isMobile && (
                  <Table.Td>
                    <Group gap={4}>
                      {d.allocazioni.map((a, i) => (
                        <Badge key={i} variant="subtle" color="teal" size="sm">
                          {areaLabel(a.area)}: {a.percentuale}%
                        </Badge>
                      ))}
                    </Group>
                  </Table.Td>
                )}
                <Table.Td>
                  <Group gap="xs" wrap="nowrap">
                    <ActionIcon variant="subtle" color="blue" size={isMobile ? 'sm' : 'md'} onClick={(e) => { e.stopPropagation(); handleEdit(d); }}>
                      <IconPencil size={isMobile ? 14 : 16} />
                    </ActionIcon>
                    <ActionIcon variant="subtle" color="red" size={isMobile ? 'sm' : 'md'} onClick={(e) => { e.stopPropagation(); handleDelete(d); }}>
                      <IconTrash size={isMobile ? 14 : 16} />
                    </ActionIcon>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Card>

      <Modal opened={showModal} onClose={() => setShowModal(false)}
             title={editing ? 'Modifica Risorsa' : 'Nuova Risorsa'} size="lg">
        <form onSubmit={handleSubmit}>
          <Stack gap="md">
            <Grid>
              <Grid.Col span={6}>
                <TextInput label="Nome" required value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput label="Cognome" value={form.cognome} onChange={e => setForm({ ...form, cognome: e.target.value })} />
              </Grid.Col>
            </Grid>
            <Checkbox label="Attivo" checked={form.attivo} onChange={e => setForm({ ...form, attivo: e.target.checked })} />
            <TextInput label="Note" value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} />

            <Paper p="md" withBorder>
              <Group justify="space-between" mb="sm">
                <Text fw={600}>Allocazioni per Area</Text>
                <Button size="xs" variant="subtle" leftSection={<IconPlus size={14} />} onClick={addAllocazione}>
                  Aggiungi Area
                </Button>
              </Group>
              {form.allocazioni.length === 0 && (
                <Text c="dimmed" size="sm">Nessuna allocazione. Clicca "Aggiungi Area".</Text>
              )}
              {form.allocazioni.map((alloc, idx) => (
                <Group key={idx} mb="xs">
                  <Select data={AREE} value={alloc.area} onChange={v => updateAllocazione(idx, 'area', v)} style={{ flex: 1 }} />
                  <NumberInput value={alloc.percentuale} onChange={v => updateAllocazione(idx, 'percentuale', v)}
                               suffix="%" min={0} max={100} style={{ width: 100 }} />
                  <ActionIcon variant="subtle" color="red" onClick={() => removeAllocazione(idx)}>
                    <IconTrash size={14} />
                  </ActionIcon>
                </Group>
              ))}
              {form.allocazioni.length > 0 && (
                <Text size="sm" c={Math.abs(form.allocazioni.reduce((s, a) => s + (a.percentuale || 0), 0) - 100) < 0.01 ? 'green' : 'red'} fw={600}>
                  Totale: {form.allocazioni.reduce((s, a) => s + (a.percentuale || 0), 0)}%
                </Text>
              )}
            </Paper>

            <Group justify="flex-end">
              <Button variant="subtle" onClick={() => setShowModal(false)}>Annulla</Button>
              <Button type="submit">Salva</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  );
}

export default Dipendenti;
